import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Info, Lock, Mail } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useLoginMutation } from "@/modules/auth/services/useLoginMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { Alert, cx, Icon } from "@/shared/rd";

/** Email + password sign-in. On success the admin lands on the dashboard. */
export function LoginForm({ locale }: { locale: string }) {
  const navigate = useNavigate();
  const t = useTranslations();
  const s = keys.shell;
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { mutateAsync: login, loginFailure, isPending } = useLoginMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(formData, ["email", "password"], t(s.required));

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await login({
        email: String(formData.get("email") ?? ""),
        password: String(formData.get("password") ?? ""),
      });
      navigate(`/${locale}/admin`, { replace: true });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        // A refused sign-in gets the design's plain sentence; anything else
        // keeps the server's own message, which says what actually happened.
        setError(error.status === 401 ? t(s.loginFailed) : error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const message = error || loginFailure ? error || loginFailure?.message || t(keys.errors.generic) : null;
  const failed = Boolean(message);
  const Arrow = locale === "ar" ? ArrowLeft : ArrowRight;

  return (
    <form noValidate onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="rd-login-label">{t(keys.common.email)}</span>
        <span className={cx("rd-login-field", (fieldErrors.email || failed) && "rd-bad")}>
          <Icon icon={Mail} size={16} color="var(--rd-text-icon)" stroke={1.8} />
          <input
            id="email"
            name="email"
            type="email"
            dir="ltr"
            autoComplete="username"
            placeholder={t(s.loginEmailPh)}
            required
            aria-invalid={Boolean(fieldErrors.email) || undefined}
          />
        </span>
        {fieldErrors.email ? <span className="rd-login-error">{fieldErrors.email}</span> : null}
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="rd-login-label">{t(keys.common.password)}</span>
        <span className={cx("rd-login-field", (fieldErrors.password || failed) && "rd-bad")}>
          <Icon icon={Lock} size={16} color="var(--rd-text-icon)" stroke={1.8} />
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            dir="ltr"
            autoComplete="current-password"
            required
            aria-invalid={Boolean(fieldErrors.password) || undefined}
          />
          <button
            type="button"
            className="rd-login-eye"
            onClick={() => setShowPassword((shown) => !shown)}
            aria-label={showPassword ? t(s.hidePassword) : t(s.showPassword)}
            title={showPassword ? t(s.hidePassword) : t(s.showPassword)}
          >
            <Icon icon={showPassword ? EyeOff : Eye} size={16} stroke={1.8} />
          </button>
        </span>
        <span className={fieldErrors.password ? "rd-login-error" : "rd-login-hint"}>{fieldErrors.password ?? t(s.loginPasswordHint)}</span>
      </label>
      {message ? <Alert>{`${message} ${t(keys.errors.recovery)}`}</Alert> : null}
      <button type="submit" className="rd-login-submit" disabled={isSubmitting || isPending}>
        <span>{t(keys.auth.loginAction)}</span>
        <Icon icon={Arrow} size={16} color="#ffffff" stroke={1.8} />
      </button>
      <p style={{ margin: "4px 0 0", display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.5, color: "var(--rd-text-tertiary)" }}>
        <Icon icon={Info} size={15} color="var(--rd-text-meta)" stroke={1.8} />
        <span>{t(s.loginFoot)}</span>
      </p>
    </form>
  );
}
